import { useEffect, useRef, useState } from "react";
import { safeRedirect } from "@/lib/safe-redirect";
import { useNavigate, useSearchParams } from "react-router-dom";
import * as Sentry from "@sentry/react";
import { oauthApi, tenantApi } from "@/lib/api/client";
import { useAuthStore } from "@/lib/auth";
import { BooleanIcon } from "@nodus/ui";
import { Text, Anchor, Stack, Flex, Loader } from "@mantine/core";

const ERROR_MESSAGES: Record<string, string> = {
  oauth_denied: "Sign-in was cancelled.",
  oauth_no_account: "No account found for your email. Contact your admin.",
  oauth_expired: "Sign-in session expired. Please try again.",
  oauth_error: "Sign-in failed. Please try again.",
  already_linked: "This account is already connected to another user.",
};

export default function OAuthCallbackPage() {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const { setAuth } = useAuthStore();
  const [error, setError] = useState("");

  const code = searchParams.get("code");
  const provider = searchParams.get("provider");
  const mode = searchParams.get("mode");
  const success = searchParams.get("success");
  const errorParam = searchParams.get("error");
  // Only a same-origin relative path (no open redirect); query and hash kept,
  // so a `/?guide=` link still opens its guide after signing in.
  const redirect = safeRedirect(searchParams.get("redirect"));

  // Link mode — show success and redirect to account
  useEffect(() => {
    if (mode === "link" && success === "1") {
      const timer = setTimeout(() => navigate("/account", { replace: true }), 1500);
      return () => clearTimeout(timer);
    }
  }, [mode, success, navigate]);

  // Error from callback
  useEffect(() => {
    if (errorParam) {
      const msg = ERROR_MESSAGES[errorParam] || "An error occurred. Please try again.";
      setError(msg);
    }
  }, [errorParam]);

  // Sign-in mode — exchange code for tokens (run once)
  const exchangedRef = useRef(false);
  useEffect(() => {
    if (!code || mode === "link" || exchangedRef.current) return;
    exchangedRef.current = true;

    const exchange = async () => {
      try {
        const res = await oauthApi.exchange(code);
        const { access, refresh } = res.data;

        // Store tokens so /me/ request is authenticated
        localStorage.setItem("access_token", access);
        localStorage.setItem("refresh_token", refresh);

        // Fetch full user profile
        const meResponse = await tenantApi.getMe();
        setAuth(meResponse.data, access, refresh);

        navigate(redirect, { replace: true });
      } catch (err) {
        Sentry.captureException(err);
        const status = (err as { response?: { status?: number } })?.response?.status;
        setError(
          status === 400
            ? "Sign-in session expired. Please try again."
            : "Sign-in failed. Please try again.",
        );
      }
    };

    exchange();
  }, [code, mode, redirect, navigate, setAuth]);

  // Link success state
  if (mode === "link" && success === "1") {
    return (
      <Flex mih="100vh" align="center" justify="center" p="md">
        <Stack align="center" gap="sm">
          <BooleanIcon value={true} size={24} />
          <Text fz="sm" c="dimmed">
            {provider ? `${provider.charAt(0).toUpperCase() + provider.slice(1)} account` : "Account"} connected
          </Text>
        </Stack>
      </Flex>
    );
  }

  if (error) {
    const backPath = mode === "link" ? "/account" : "/login";
    const backLabel = mode === "link" ? "Back to account" : "Back to login";
    return (
      <Flex mih="100vh" align="center" justify="center" p="md">
        <Stack align="center" gap="sm">
          <Text fz="sm" c="red">{error}</Text>
          <Anchor href={backPath} fz="sm" td="underline">
            {backLabel}
          </Anchor>
        </Stack>
      </Flex>
    );
  }

  return (
    <Flex mih="100vh" align="center" justify="center" p="md">
      <Stack align="center" gap="sm">
        <Loader size={24} />
        <Text fz="sm" c="dimmed">Signing you in...</Text>
      </Stack>
    </Flex>
  );
}
